import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { tablaService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const TABLAS_CONFIG = {
  categorias:         { title: 'Categorias',         singular: 'Categoria',         id_field: 'id_categoria',     apiKey: 'categorias',       subtitle: 'Tablas Generales' },
  marcas:             { title: 'Marcas',              singular: 'Marca',             id_field: 'id_marca',         apiKey: 'marcas',           subtitle: 'Tablas Generales' },
  modelos:            { title: 'Modelos',             singular: 'Modelo',            id_field: 'id_modelo',        apiKey: 'modelos',          subtitle: 'Tablas Generales' },
  familias:           { title: 'Familias',            singular: 'Familia',           id_field: 'id_familia',       apiKey: 'familias',         subtitle: 'Tablas Generales' },
  lineas:             { title: 'Lineas',              singular: 'Linea',             id_field: 'id_linea',         apiKey: 'lineas',           subtitle: 'Tablas Generales' },
  grupos:             { title: 'Grupos',              singular: 'Grupo',             id_field: 'id_grupo',         apiKey: 'grupos',           subtitle: 'Tablas Generales' },
  'centros-costo':    { title: 'Centros de Costo',    singular: 'Centro de Costo',   id_field: 'id_centro_costo',  apiKey: 'centros-costo',    subtitle: 'Tablas Generales' },
  'tipos-existencia': { title: 'Tipos de Existencia', singular: 'Tipo de Existencia', id_field: 'id_tipo_existencia', apiKey: 'tipos-existencia', subtitle: 'Tablas Generales' },
  areas:              { title: 'Areas',               singular: 'Area',              id_field: 'id_area',          apiKey: 'areas',            subtitle: 'Utilitarios' },
  prioridades:        { title: 'Prioridades',         singular: 'Prioridad',         id_field: 'id_prioridad',     apiKey: 'prioridades',      subtitle: 'Utilitarios' },
  'tipos-documento':  { title: 'Tipo Doc. Comerciales', singular: 'Tipo Doc. Comercial', id_field: 'id_tipo_documento', apiKey: 'tipos-documento',  subtitle: 'Utilitarios' },
  'tipos-operacion':  { title: 'Tipos de Operación',   singular: 'Tipo de Operación',  id_field: 'id_tipo_operacion', apiKey: 'tipos-operacion',  subtitle: 'Utilitarios' },
  'motivos-traslado': { title: 'Motivos de Traslado',  singular: 'Motivo de Traslado', id_field: 'id_motivo_traslado', apiKey: 'motivos-traslado', subtitle: 'Utilitarios' },
  cargos:             { title: 'Cargos',               singular: 'Cargo',              id_field: 'id_cargo',         apiKey: 'cargos',           subtitle: 'Utilitarios' },
};

const TablaSimple = ({ tablaKey }) => {
  const config = TABLAS_CONFIG[tablaKey];
  const service = tablaService(config.apiKey);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ codigo: '', descripcion: '' });

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await service.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: `Error al cargar ${config.title.toLowerCase()}.` });
    } finally {
      setLoading(false);
    }
  }, [tablaKey]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const codigo = row['Codigo'] || row['codigo'] || row['CODIGO'] || '';
        const descripcion = row['Descripcion'] || row['descripcion'] || row['DESCRIPCION'] || '';
        let errorMsg = null;
        if (!codigo && !descripcion) {
          fail++; errorMsg = `Fila ${i + 1}: Codigo y Descripcion vacios`;
        } else if (!codigo) {
          fail++; errorMsg = `Fila ${i + 1}: Codigo vacio`;
        } else if (!descripcion) {
          fail++; errorMsg = `Fila ${i + 1}: Descripcion vacia`;
        } else {
          try {
            await service.create({ codigo: String(codigo), descripcion: String(descripcion) });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} (${codigo}): ${getErrorDetail(err, 'Error al crear registro')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchRecords();
    };
    return () => { window._dtImportHandler = null; };
  }, [tablaKey]);

  const openCreate = () => { setForm({ codigo: '', descripcion: '' }); setEditing(null); setShowModal(true); };

  const openEdit = (record) => {
    setEditing(record);
    setForm({ codigo: record.codigo, descripcion: record.descripcion });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.descripcion) {
      setAlert({ type: 'warning', message: 'Codigo y descripcion son obligatorios.' });
      return;
    }
    try {
      if (editing) {
        await service.update(editing[config.id_field], form);
      } else {
        await service.create(form);
      }
      setShowModal(false);
      fetchRecords();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || `Error al guardar.` });
    }
  };

  const handleDelete = (record) => {
    setConfirm({
      title: `Eliminar ${config.singular}`,
      message: `¿Eliminar "${record.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await service.delete(record[config.id_field]);
          setToast(`${config.singular} eliminado`);
          fetchRecords();
        } catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 120, style: { fontWeight: 600 } },
    { key: 'descripcion', label: 'Descripcion' },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: true, example: '001', description: 'Codigo unico del registro (max 20 caracteres)' },
    { key: 'Descripcion', label: 'Descripcion', required: true, example: `${config.singular} ejemplo`, description: `Nombre o descripcion de la ${config.singular.toLowerCase()}` },
  ];

  return (
    <Layout title={config.title} subtitle={config.subtitle || 'Tablas Generales'}>
      <div className="page-header">
        <h2>{config.title}</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable
        columns={columns}
        data={records}
        idField={config.id_field}
        onEdit={openEdit}
        onDelete={handleDelete}
        loading={loading}
        emptyMessage="No hay registros"
        exportFileName={config.apiKey}
        importColumns={importCols}
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? `Editar ${config.singular}` : config.singular}</h2>
                {!editing && (
                  <span className="modal-header-badge">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg>
                  </span>
                )}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Codigo *</label>
                  <input value={form.codigo} onChange={(e) => setForm({...form, codigo: e.target.value})} placeholder="Ej: 001" autoFocus />
                </div>
                <div className="form-row">
                  <label>Descripcion *</label>
                  <input value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} placeholder={`Nombre de ${config.singular.toLowerCase()}`} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export { TABLAS_CONFIG };
export default TablaSimple;
